import moment from 'moment';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import i18n from 'i18n';
import { NameWithIdColumn } from 'components/TableColumn/NameWithIdColumn';
import { ColumnDescription } from 'components/common/Table/types';
import {
  renderColumn,
  sortableColumn
} from 'components/TableColumn/TableColumn';
import {
  DeviceUnitLevel,
  DeviceUnitRecord,
  DeviceUnitSortOrder
} from 'core/deviceManagement/DeviceManagement';
import { DeviceStatusChip } from './DeviceStatusChip';
import {
  buildWeeklyBusinessHours,
  formatTodayBusinessHours
} from './businessHoursFormat';
import { NO_VALUE } from './deviceDetail';
import styles from './deviceManagement.module.scss';

export enum DeviceListColumn {
  UNIT_NAME = 'unitName',
  STORE_NUM = 'storeNum',
  DEVICE_CHARACTERISTIC = 'deviceCharacteristic',
  HEALTH_STATE = 'healthState',
  ADDRESS = 'address',
  TODAY_BUSINESS_HOURS = 'todayBusinessHours',
  LAST_REPORTED_AT = 'lastReportedAt',
  DETAIL = 'detail'
}

/**
 * 預設排序：狀態由重到輕（healthState 是依嚴重度排，不是字母序），同狀態再依 unitKey。
 *
 * 清單的初始查詢與匯出面板上那句「依預設排序（狀態異常優先）」都指著這一份，
 * 不各寫一份 —— 分開寫的話改了其中一邊，另一邊會變成一句沒人發現的假話。
 */
export const DEFAULT_DEVICE_SORT: {
  sortField: string;
  sortOrder: DeviceUnitSortOrder;
} = {
  sortField: DeviceListColumn.HEALTH_STATE,
  sortOrder: 'asc'
};

/**
 * 清單上排不動的欄位。`DETAIL` 是操作欄，沒有對應的資料欄位；今日營業時間是從一週的
 * 營業時間依「今天星期幾」現算出來的，後端沒有這個排序鍵。
 */
const UNSORTABLE_LIST_COLUMNS: string[] = [
  DeviceListColumn.DETAIL,
  DeviceListColumn.TODAY_BUSINESS_HOURS
];

/** 清單檢視排得動的欄位。 */
export const LIST_SORT_FIELDS: string[] = Object.values(
  DeviceListColumn
).filter((column) => !UNSORTABLE_LIST_COLUMNS.includes(column));

/**
 * 表格回報的排序。
 *
 * 同一欄點第三次會把排序清掉，那時表格回報的是 `undefined` —— 而
 * `TableChangeState` 宣告的型別是 `string`，回報處的兩個 `as any` 讓這件事在編譯期
 * 完全沒有訊號。原樣寫進查詢條件，送出去的就是字面上的 `sort=undefined,undefined`，
 * 後端的白名單認不得而回 400，畫面上只看得到一個英文錯誤訊息。
 *
 * 清掉排序就回到預設排序：後端沒收到 sort 時套的也是同一份，兩邊講的是同一件事。
 * 表格自己的箭頭這時是空的，而資料照預設排序回來 —— 與第一次進頁面看到的一致。
 */
export function resolveTableSort(
  sortField?: string,
  sortOrder?: DeviceUnitSortOrder
): { sortField: string; sortOrder: DeviceUnitSortOrder } {
  return sortField && sortOrder
    ? { sortField, sortOrder }
    : DEFAULT_DEVICE_SORT;
}

/** 唯一鍵。門市名稱會重複，不能拿來當列的 key。 */
export const DEVICE_UNIT_KEY_FIELD = 'unitKey';

export const DEVICE_LIST_DATE_FORMAT = 'YYYY-MM-DD HH:mm';

/**
 * 決定這份清單是門市層還是機台層。
 *
 * 以回應每一列的 `unitLevel` 為準，不從資料推（例如拿 `deviceCharacteristic`
 * 有沒有值去猜）—— 機台層本來就可能有機台沒填特徵，猜出來會少一整欄。
 * 清單一列都沒有時才退回渠道的投遞單位，讓空清單的表頭不會少一欄。
 */
export function resolveDeviceUnitLevel(
  records: DeviceUnitRecord[],
  channelDeliveryUnit?: DeviceUnitLevel
): DeviceUnitLevel {
  return records[0]?.unitLevel ?? channelDeliveryUnit ?? DeviceUnitLevel.STORE;
}

const textFormatter = (cell?: string) => <>{cell ? cell : NO_VALUE}</>;

const dateTimeFormatter = (cell?: string) => (
  <>{cell ? moment(cell).format(DEVICE_LIST_DATE_FORMAT) : NO_VALUE}</>
);

/**
 * 名稱欄。點名稱與點「詳情」是同一個動作，所以共用同一個 handler。
 *
 * 可點的名稱走共用的 `NameWithIdColumn`，顏色與其他清單頁一致；這裡沒有 id 好顯示，
 * 所以只給名字。
 *
 * 兩個分頁共用這一支，差別只在名稱後面接不接標注（`renderSuffix`）——
 * 可售狀況那一頁要標「被 PMP 買走」，機台清單檢視不標。各寫一份的話，欄位標題、可點與否、
 * 空值怎麼顯示這幾件事會各自漂移。
 */
export const unitNameColumn = (
  unitLevel: DeviceUnitLevel,
  onShowDetail: (record: DeviceUnitRecord) => void,
  renderSuffix?: (record: DeviceUnitRecord) => React.ReactNode
): ColumnDescription =>
  renderColumn(
    sortableColumn(
      DeviceListColumn.UNIT_NAME,
      unitLevel === DeviceUnitLevel.DEVICE
        ? i18n.t('deviceManagement.headers.deviceName')
        : i18n.t('deviceManagement.headers.unitName')
    ),
    (cell: string, record: DeviceUnitRecord) => {
      const name = (
        <NameWithIdColumn
          name={cell || NO_VALUE}
          clickable
          onClick={() => onShowDetail(record)}
        />
      );
      const suffix = renderSuffix?.(record);
      if (!suffix) {
        return name;
      }
      return (
        <span className={styles.unitNameCell}>
          {name}
          {suffix}
        </span>
      );
    }
  );

export const storeNumColumn = (): ColumnDescription =>
  renderColumn(
    sortableColumn(
      DeviceListColumn.STORE_NUM,
      i18n.t('deviceManagement.headers.storeNum')
    ),
    textFormatter
  );

export const healthStateColumn = (): ColumnDescription =>
  renderColumn(
    sortableColumn(
      DeviceListColumn.HEALTH_STATE,
      i18n.t('deviceManagement.headers.healthState')
    ),
    (cell: string, record: DeviceUnitRecord) => (
      <DeviceStatusChip healthState={cell} unitKey={record.unitKey} />
    )
  );

/**
 * 今日營業時間。滑過去看一週七天，沒有營業時間資料時只顯示沒有值，不掛提示 ——
 * 七列都寫沒有值不比一個沒有值多說什麼。
 */
const TodayBusinessHoursCell: React.FC<{ record: DeviceUnitRecord }> = ({
  record
}) => {
  const weekly = buildWeeklyBusinessHours(record.businessHours);
  const today = (
    <span data-testid={`today-business-hours-${record.unitKey}`}>
      {formatTodayBusinessHours(record.businessHours)}
    </span>
  );
  if (!weekly) {
    return today;
  }
  return (
    <OverlayTrigger
      placement='top'
      trigger={['hover', 'focus']}
      overlay={
        <Tooltip
          id={`device-business-hours-${record.unitKey}`}
          className={styles.businessHoursTooltip}
        >
          {weekly.map((row) => (
            <div key={row.dayOfWeek}>{row.text}</div>
          ))}
        </Tooltip>
      }
    >
      {today}
    </OverlayTrigger>
  );
};

export const todayBusinessHoursColumn = (): ColumnDescription =>
  renderColumn(
    sortableColumn(
      DeviceListColumn.TODAY_BUSINESS_HOURS,
      i18n.t('deviceManagement.headers.todayBusinessHours'),
      false
    ),
    (_cell: unknown, record: DeviceUnitRecord) => (
      <TodayBusinessHoursCell record={record} />
    )
  );

export const detailColumn = (
  onShowDetail: (record: DeviceUnitRecord) => void
): ColumnDescription =>
  renderColumn(
    sortableColumn(DeviceListColumn.DETAIL, '', false),
    (_cell: unknown, record: DeviceUnitRecord) => (
      <span
        className={styles.detailTrigger}
        onClick={() => onShowDetail(record)}
        data-testid={`detail-trigger-${record.unitKey}`}
      >
        {i18n.t('deviceManagement.headers.detail')}
      </span>
    )
  );

export function buildDeviceListColumns(
  unitLevel: DeviceUnitLevel,
  onShowDetail: (record: DeviceUnitRecord) => void
): ColumnDescription[] {
  const characteristicColumn = renderColumn(
    sortableColumn(
      DeviceListColumn.DEVICE_CHARACTERISTIC,
      i18n.t('deviceManagement.headers.deviceCharacteristic')
    ),
    textFormatter
  );

  return [
    unitNameColumn(unitLevel, onShowDetail),
    storeNumColumn(),
    healthStateColumn(),
    renderColumn(
      sortableColumn(
        DeviceListColumn.ADDRESS,
        i18n.t('deviceManagement.headers.address')
      ),
      textFormatter
    ),
    todayBusinessHoursColumn(),
    // 機台特徵只有機台層的渠道才有這一欄；門市層沒有這個維度。
    ...(unitLevel === DeviceUnitLevel.DEVICE ? [characteristicColumn] : []),
    renderColumn(
      sortableColumn(
        DeviceListColumn.LAST_REPORTED_AT,
        i18n.t('deviceManagement.headers.lastReportedAt')
      ),
      dateTimeFormatter
    ),
    detailColumn(onShowDetail)
  ];
}
